import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Plus, Trash2 } from "lucide-react";

const newKey = () => Math.random().toString(36).slice(2, 10);
const emptyGuest = (leader = false, departure = "") => ({
  _key: newKey(),
  guest_type: leader ? "CG" : "MG", first_name: "", last_name: "", date_of_birth: "", gender: "M", citizenship: "Italiana",
  place_of_birth: "", province_of_birth: "", country_of_birth: "Italia",
  document_type: "CI", document_number: "", document_issue_place: "", document_issue_date: "", departure_date: departure, is_group_leader: leader,
});

const itDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

export default function Ospiti() {
  const { bookingId, token } = useParams();
  const [info, setInfo] = useState(null);
  const [guests, setGuests] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/guests/${token}`)
      .then((r) => { setInfo(r.data); setGuests(r.data.guests?.length ? r.data.guests.map((g) => ({ ...emptyGuest(false, r.data.check_out), ...g, departure_date: g.departure_date || r.data.check_out, guest_type: g.guest_type || (g.is_group_leader ? "CG" : "MG") })) : [emptyGuest(true, r.data.check_out)]); })
      .catch((e) => setError(e?.response?.data?.detail || "Link non valido"));
  }, [bookingId, token]);

  const update = (i, f, v) => setGuests(guests.map((g, ix) => {
    if (ix !== i) return f === "guest_type" && v === "CG" ? { ...g, guest_type: "MG", is_group_leader: false } : g;
    const n = { ...g, [f]: v };
    if (f === "guest_type") n.is_group_leader = v === "CG";
    return n;
  }));
  const add = () => setGuests([...guests, emptyGuest(false, info?.check_out || "")]);
  const remove = (i) => setGuests(guests.filter((_, ix) => ix !== i));
  const REQUIRED = ["first_name", "last_name", "date_of_birth", "place_of_birth", "province_of_birth", "country_of_birth", "citizenship", "document_number", "document_issue_place", "document_issue_date", "departure_date"];
  const leaders = guests.filter((g) => g.guest_type === "CG").length;
  const departureInvalid = (g) => info && g.departure_date && g.departure_date <= info.check_in;
  const valid = guests.length > 0 && guests.every((g) => REQUIRED.every((k) => (g[k] || "").toString().trim()) && !departureInvalid(g)) && leaders === 1;
  const overflow = info && guests.length > info.num_participants;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/guests/${token}`, { guests: guests.map(({ _key, ...g }) => g) });
      setDone(data);
      toast.success("Dati ospiti inviati");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : e?.response ? `Errore ${e.response.status}` : "Connessione non riuscita: controlla la rete e riprova";
      toast.error(msg, { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20" data-testid="guests-page">
        {error && (
          <div className="text-center" data-testid="guests-error">
            <AlertTriangle size={48} className="mx-auto text-[#8C3A27]" />
            <h1 className="font-display text-4xl mt-6">Link non valido</h1>
            <p className="mt-4 text-[#6B6960]">{error}</p>
          </div>
        )}
        {!error && !info && <div className="text-center text-[#6B6960]">Caricamento…</div>}
        {!error && info && done && (
          <div className="text-center" data-testid="guests-success">
            <CheckCircle2 size={56} className="mx-auto text-[#8C3A27]" />
            <h1 className="font-display text-5xl mt-6">Grazie!</h1>
            <p className="mt-4 text-[#6B6960]">Abbiamo registrato {done.count} ospiti per la pratica <strong>{info.reference}</strong>. Può tornare su questo link per aggiornare i dati fino al giorno dell'arrivo.</p>
            <Button onClick={() => setDone(null)} variant="outline" className="rounded-full mt-8">Modifica i dati</Button>
          </div>
        )}
        {!error && info && !done && (
          <div data-testid="guests-form">
            <div className="overline">Registrazione ospiti · art. 109 TULPS</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-3">Modulo ospiti</h1>
            <p className="mt-4 text-[#6B6960] max-w-2xl">
              Pratica <strong>{info.reference}</strong>{info.organization ? ` · ${info.organization}` : ""} · soggiorno {info.check_in} → {info.check_out} · {info.num_participants} partecipanti.
              Inserisca i dati di ogni ospite come riportati sul documento d'identità. Il capogruppo è contrassegnato con ★.
            </p>
            {info.guests_submitted_at && <p className="mt-2 text-sm text-green-800">Dati già inviati il {new Date(info.guests_submitted_at).toLocaleString("it-IT")}: può aggiornarli e reinviarli.</p>}

            <div className="mt-8 space-y-4">
              {guests.map((g, i) => (
                <div key={g._key} className="border border-[#E2DFD6] bg-white p-5" data-testid={`guest-${i}`}>
                  <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div className="font-display text-xl">Ospite {i + 1} {g.guest_type === "CG" && <span className="text-[#8C3A27]">★ Capo Gruppo</span>}</div>
                    <div className="flex items-center gap-3">
                      <div className="w-44">
                        <Select value={g.guest_type} onValueChange={(v) => update(i, "guest_type", v)}>
                          <SelectTrigger data-testid={`guest-${i}-type`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="CG">Capo Gruppo</SelectItem><SelectItem value="MG">Membro del gruppo</SelectItem></SelectContent>
                        </Select>
                      </div>
                      {guests.length > 1 && <button type="button" onClick={() => remove(i)} className="text-[#8C3A27]" data-testid={`guest-remove-${i}`}><Trash2 size={16} /></button>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><Label>Cognome *</Label><Input value={g.last_name} onChange={(e) => update(i, "last_name", e.target.value)} data-testid={`guest-${i}-last-name`} /></div>
                    <div><Label>Nome *</Label><Input value={g.first_name} onChange={(e) => update(i, "first_name", e.target.value)} data-testid={`guest-${i}-first-name`} /></div>
                    <div><Label>Sesso *</Label>
                      <Select value={g.gender} onValueChange={(v) => update(i, "gender", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="M">M</SelectItem><SelectItem value="F">F</SelectItem></SelectContent></Select></div>
                    <div><Label>Data di nascita *</Label><Input type="date" value={g.date_of_birth} onChange={(e) => update(i, "date_of_birth", e.target.value)} data-testid={`guest-${i}-dob`} /></div>
                    <div><Label>Luogo di nascita *</Label><Input value={g.place_of_birth} onChange={(e) => update(i, "place_of_birth", e.target.value)} data-testid={`guest-${i}-pob`} /></div>
                    <div><Label>Provincia di nascita *</Label><Input maxLength={2} placeholder="es. PU (EE se estero)" value={g.province_of_birth} onChange={(e) => update(i, "province_of_birth", e.target.value.toUpperCase())} data-testid={`guest-${i}-prov`} /></div>
                    <div><Label>Stato di nascita *</Label><Input value={g.country_of_birth} onChange={(e) => update(i, "country_of_birth", e.target.value)} data-testid={`guest-${i}-country`} /></div>
                    <div><Label>Cittadinanza *</Label><Input value={g.citizenship} onChange={(e) => update(i, "citizenship", e.target.value)} data-testid={`guest-${i}-citizenship`} /></div>
                    <div><Label>Tipo documento *</Label>
                      <Select value={g.document_type} onValueChange={(v) => update(i, "document_type", v)}><SelectTrigger data-testid={`guest-${i}-doctype`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="CI">Carta d'Identità</SelectItem><SelectItem value="Passport">Passaporto</SelectItem></SelectContent></Select></div>
                    <div><Label>Numero documento *</Label><Input value={g.document_number} onChange={(e) => update(i, "document_number", e.target.value)} data-testid={`guest-${i}-doc`} /></div>
                    <div><Label>Luogo di rilascio *</Label><Input value={g.document_issue_place} onChange={(e) => update(i, "document_issue_place", e.target.value)} data-testid={`guest-${i}-issue`} /></div>
                    <div><Label>Data di rilascio *</Label><Input type="date" value={g.document_issue_date} onChange={(e) => update(i, "document_issue_date", e.target.value)} data-testid={`guest-${i}-issue-date`} /></div>
                    <div className="col-span-2 md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-[#E2DFD6] pt-3 mt-1">
                      <div><Label>Data di arrivo</Label><Input value={itDate(info.check_in)} disabled className="bg-[#F3F1EC]" /></div>
                      <div>
                        <Label>Data di partenza * <span className="text-[#6B6960] font-normal">({itDate(g.departure_date)})</span></Label>
                        <Input type="date" min={info.check_in} value={g.departure_date} onChange={(e) => update(i, "departure_date", e.target.value)} className={departureInvalid(g) ? "border-red-600" : ""} data-testid={`guest-${i}-departure`} />
                        {departureInvalid(g) && <p className="text-xs text-red-700 mt-1" data-testid={`guest-${i}-departure-error`}>La partenza deve essere successiva all'arrivo.</p>}
                      </div>
                      <div className="col-span-2 text-xs text-[#6B6960] self-end pb-2">Precompilata con la partenza del gruppo ({itDate(info.check_out)}): modificala solo se l'ospite parte in un giorno diverso.</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <Button variant="outline" onClick={add} disabled={overflow || guests.length >= info.num_participants} className="rounded-full" data-testid="guest-add"><Plus size={16} className="mr-2" /> Aggiungi ospite ({guests.length}/{info.num_participants})</Button>
              <Button onClick={submit} disabled={!valid || overflow || busy} className="btn-primary-cdm rounded-full h-11 px-7" data-testid="guests-submit">{busy ? "Invio…" : "Invia i dati ospiti"}</Button>
            </div>
            {overflow && <p className="text-sm text-red-700 mt-2">Il numero di ospiti supera i partecipanti previsti ({info.num_participants}).</p>}
            {guests.length > 0 && leaders !== 1 && <p className="text-sm text-red-700 mt-2" data-testid="guests-leader-error">Indicare esattamente un Capo Gruppo.</p>}
          </div>
        )}
      </section>
    </Layout>
  );
}
